import React from 'react';
import type { UseFormRegister, UseFormSetValue, UseFormWatch, FieldErrors, UseFormTrigger } from 'react-hook-form';
import { LabelInput } from '@/components/ui/inputs';
import { Selector } from '@/components/ui/inputs/selector';
import { DatePicker } from '@/components/ui/filters/DatePicker';
import { PrimaryButton } from '@/components/ui/buttons/PrimaryButton';
import { FormLayout, PermissionGuard } from '@/components/layout';
import { useTranslation } from '@/i18n';
import { normalizeSelectToNumber } from '@/utils/formHelpers';
import type { UpdateEmployeeContractFormData } from '@/schemas/employee/employeeContractEdit.schema';


interface EditEmployeeContractFormProps {
  contractForm: {
    register: UseFormRegister<UpdateEmployeeContractFormData>;
    setValue: UseFormSetValue<UpdateEmployeeContractFormData>;
    watch: UseFormWatch<UpdateEmployeeContractFormData>;
    errors: FieldErrors<UpdateEmployeeContractFormData>;
    trigger: UseFormTrigger<UpdateEmployeeContractFormData>;
  };
  contractStatusOptions: { value: string; label: string }[];
  contractTypeOptions: { value: string; label: string }[];
  contractModalityOptions: { value: string; label: string }[];
  companyOptions: { value: string; label: string }[];
  areaOptions: { value: string; label: string }[];
  positionOptions: { value: string; label: string }[];
  costCenterOptions: { value: string; label: string }[];
  unionOptions: { value: string; label: string }[];
  updating: boolean;
  onCancel: () => void;
  onSave: () => void;
}

export const EditEmployeeContractForm: React.FC<EditEmployeeContractFormProps> = ({
  contractForm,
  contractStatusOptions,
  contractTypeOptions,
  contractModalityOptions,
  companyOptions,
  areaOptions,
  positionOptions,
  costCenterOptions,
  unionOptions,
  updating,
  onCancel,
  onSave,
}) => {
  const { t: translate } = useTranslation();

  const handleSubmit = (event: React.FormEvent) => {
    event.preventDefault();
  };

  return (
    <FormLayout onSubmit={handleSubmit} columns={4}>
      {/* Sección 1: Datos del contrato */}
      <FormLayout.Section title="Datos del contrato">
        <Selector
          label={translate('employee:contracts.fields.contractStatus')}
          required
          options={contractStatusOptions}
          value={contractForm.watch('contractStatusId')?.toString() || ''}
          onChange={(value: string | string[]) => {
            const numericValue = normalizeSelectToNumber(value);
            contractForm.setValue('contractStatusId', numericValue || 0, { shouldValidate: true, shouldDirty: true });
          }}
          color={contractForm.errors.contractStatusId ? 'error' : 'default'}
          supportingText={
            contractForm.errors.contractStatusId?.message
              ? translate(contractForm.errors.contractStatusId.message)
              : undefined
          }
          data-testid="contract-status-select"
        />

        <Selector
          label={translate('employee:contracts.fields.companyContract')}
          required
          options={companyOptions}
          value={contractForm.watch('companyContractId')?.toString() || ''}
          onChange={(value: string | string[]) => {
            const numericValue = normalizeSelectToNumber(value);
            contractForm.setValue('companyContractId', numericValue || 0, { shouldValidate: true, shouldDirty: true });
          }}
          color={contractForm.errors.companyContractId ? 'error' : 'default'}
          supportingText={
            contractForm.errors.companyContractId?.message
              ? translate(contractForm.errors.companyContractId.message)
              : undefined
          }
          data-testid="company-contract-select"
        />

        <LabelInput
          label={translate('employee:contracts.fields.contractName')}
          required
          color={contractForm.errors.contractName ? 'error' : undefined}
          supportingText={
            contractForm.errors.contractName?.message
              ? translate(contractForm.errors.contractName.message)
              : undefined
          }
          value={contractForm.watch('contractName') || ''}
          onChange={(value: string) => contractForm.setValue('contractName', value, { shouldValidate: true, shouldDirty: true })}
          placeholder={translate('employee:contracts.fields.contractNamePlaceholder')}
          data-testid="contract-name-input"
        />

        <LabelInput
          label={translate('employee:contracts.fields.contractNumber')}
          required
          value={contractForm.watch('contractNumber')?.toString() || ''}
          onChange={() => {}}
          disabled
          data-testid="contract-number-input"
        />

        <Selector
          label={translate('employee:contracts.fields.contractType')}
          required
          options={contractTypeOptions}
          value={contractForm.watch('contractTypeId')?.toString() || ''}
          onChange={(value: string | string[]) => {
            const numericValue = normalizeSelectToNumber(value);
            contractForm.setValue('contractTypeId', numericValue || 0, { shouldValidate: true, shouldDirty: true });
          }}
          color={contractForm.errors.contractTypeId ? 'error' : 'default'}
          supportingText={
            contractForm.errors.contractTypeId?.message
              ? translate(contractForm.errors.contractTypeId.message)
              : undefined
          }
          data-testid="contract-type-select"
        />

        <Selector
          label={translate('employee:contracts.fields.contractModality')}
          required
          options={contractModalityOptions}
          value={contractForm.watch('contractModalityId')?.toString() || ''}
          onChange={(value: string | string[]) => {
            const numericValue = normalizeSelectToNumber(value);
            contractForm.setValue('contractModalityId', numericValue || 0, { shouldValidate: true, shouldDirty: true });
          }}
          color={contractForm.errors.contractModalityId ? 'error' : 'default'}
          supportingText={
            contractForm.errors.contractModalityId?.message
              ? translate(contractForm.errors.contractModalityId.message)
              : undefined
          }
          data-testid="contract-modality-select"
        />

        <DatePicker
          label={translate('employee:contracts.fields.dateEntry')}
          value={contractForm.watch('dateEntry') || undefined}
          onChange={(date: string) => contractForm.setValue('dateEntry', date, { shouldValidate: true, shouldDirty: true })}
          color={contractForm.errors.dateEntry ? 'error' : undefined}
          supportingText={
            contractForm.errors.dateEntry?.message
              ? translate(contractForm.errors.dateEntry.message)
              : undefined
          }
          placeholder="dd/mm/aaaa"
          closeOnSelect
          required
        />

        <DatePicker
          label={translate('employee:contracts.fields.contractStartDate')}
          value={contractForm.watch('contractStartDate') || undefined}
          onChange={(date: string) => contractForm.setValue('contractStartDate', date, { shouldValidate: true, shouldDirty: true })}
          color={contractForm.errors.contractStartDate ? 'error' : undefined}
          supportingText={
            contractForm.errors.contractStartDate?.message
              ? translate(contractForm.errors.contractStartDate.message)
              : undefined
          }
          placeholder="dd/mm/aaaa"
          closeOnSelect
          required
        />

        <DatePicker
          label={translate('employee:contracts.fields.contractEndDate')}
          value={contractForm.watch('contractEndDate') || undefined}
          onChange={(date: string) => contractForm.setValue('contractEndDate', date, { shouldValidate: true, shouldDirty: true })}
          color={contractForm.errors.contractEndDate ? 'error' : undefined}
          supportingText={
            contractForm.errors.contractEndDate?.message
              ? translate(contractForm.errors.contractEndDate.message)
              : undefined
          }
          placeholder="dd/mm/aaaa"
          closeOnSelect
        />
      </FormLayout.Section>

      {/* Sección 2: Datos organizacionales */}
      <FormLayout.Section title="Datos organizacionales">
        <Selector
          label={translate('employee:contracts.fields.area')}
          required
          options={areaOptions}
          value={contractForm.watch('areaId')?.toString() || ''}
          onChange={(value: string | string[]) => {
            const numericValue = normalizeSelectToNumber(value);
            contractForm.setValue('areaId', numericValue || 0, { shouldValidate: true, shouldDirty: true });
          }}
          color={contractForm.errors.areaId ? 'error' : 'default'}
          supportingText={
            contractForm.errors.areaId?.message
              ? translate(contractForm.errors.areaId.message)
              : undefined
          }
          data-testid="area-select"
        />

        <Selector
          label={translate('employee:contracts.fields.position')}
          required
          disabled={!contractForm.watch('areaId')}
          options={positionOptions}
          value={contractForm.watch('positionId')?.toString() || ''}
          onChange={(value: string | string[]) => {
            const numericValue = normalizeSelectToNumber(value);
            contractForm.setValue('positionId', numericValue || 0, { shouldValidate: true, shouldDirty: true });
          }}
          color={contractForm.errors.positionId ? 'error' : 'default'}
          supportingText={
            contractForm.errors.positionId?.message
              ? translate(contractForm.errors.positionId.message)
              : undefined
          }
          data-testid="position-select"
        />

        <Selector
          label={translate('employee:contracts.fields.costCenter')}
          required
          options={costCenterOptions}
          value={contractForm.watch('costCenterId')?.toString() || ''}
          onChange={(value: string | string[]) => {
            const numericValue = normalizeSelectToNumber(value);
            contractForm.setValue('costCenterId', numericValue || 0, { shouldValidate: true, shouldDirty: true });
          }}
          color={contractForm.errors.costCenterId ? 'error' : 'default'}
          supportingText={
            contractForm.errors.costCenterId?.message
              ? translate(contractForm.errors.costCenterId.message)
              : undefined
          }
          data-testid="cost-center-select"
        />

        <Selector
          label={translate('employee:contracts.fields.union')}
          required
          options={unionOptions}
          value={contractForm.watch('unionCode') || ''}
          onChange={(value: string | string[]) => {
            const stringValue = Array.isArray(value) ? value[0] : value;
            contractForm.setValue('unionCode', stringValue || '', { shouldValidate: true, shouldDirty: true });
          }}
          color={contractForm.errors.unionCode ? 'error' : 'default'}
          supportingText={
            contractForm.errors.unionCode?.message
              ? translate(contractForm.errors.unionCode.message)
              : undefined
          }
          data-testid="union-select"
        />
      </FormLayout.Section>

      {/* Action buttons */}
      <FormLayout.Footer
        cancelButton={
          <PrimaryButton type="button" variant="outline" onClick={onCancel} disabled={updating}>
            {translate('common:actions.cancel')}
          </PrimaryButton>
        }
        primaryButton={
          <PermissionGuard action="edit">
            <PrimaryButton type="button" onClick={onSave} disabled={updating} isLoading={updating}>
              {translate('common:actions.save')}
            </PrimaryButton>
          </PermissionGuard>
        }
      />
    </FormLayout>
  );
};

export default EditEmployeeContractForm;
